import Button, { IButton } from "../Button/Button";
import PriceExample, { IPriceExample } from "../PriceExample/PriceExample";
import Prices, { IPrice } from "../Prices/Prices";
import SellingPoints, { ISellingPoints } from "../SellingPoints/SellingPoints";

import Media from "../../../../CmsComponents/Media/Media";
import React, { useContext } from "react";
import RichText from "../../../../CmsComponents/RichText/RichText";
import { ISlider } from "../Slider/Slider";
import SectionWrapper from "./SectionWrapper";
import SectionWrapperText from "./SectionWrapperText";
import SectionWrapperImage from "./SectionWrapperImage";
import SectionPageContext from "../../SectionPageContext";
import SelectableContent, { ISelectableContentList } from "../SelectableContent/SelectableContent";
import { BNBox, Faq, IFaq } from "@bn-monorepo/js-packages";
import { useTheme } from "@mui/material";
import SliderSsrRenderOnly from "../Slider/SliderSsrRenderOnly";
import GoogleReview from "../../../../CmsComponents/GoogleReview/GoogleReview";

export interface Image {
  id?: string;
  url: string;
  alt: string;
  width?: number;
  height?: number;
}
interface RTE {
  html: string;
  hidden: { smDown: boolean; smUp: boolean };
}

interface BackgroundColor {
  color: string;
  label:
    | "blue"
    | "bluegradient"
    | "red"
    | "white"
    | "lightblue1"
    | "lightblue2"
    | "lightyellow1"
    | "lightblue3"
    | "lightgray1";
}

export interface HiddenOptions {
  smDown: boolean;
  smUp: boolean;
}
interface ExpandableItem {
  title: string;
  richText: string;
}

interface IExpandableList {
  list: ExpandableItem[];
  settings: any;
}
export interface ISection {
  type?: "section";
  anchorId: string;
  image?: Image;
  sectionContainerStylesOverride?: string;
  imageOrderReverse: boolean;
  backgroundColor: BackgroundColor;
  list: any[];
  imageHidden: HiddenOptions;
  isFirst?: boolean;
}

const Section: React.FC<ISection> = (props) => {
  const theme = useTheme();
  const {
    anchorId,
    backgroundColor,
    image,
    sectionContainerStylesOverride,
    imageOrderReverse,
    list,
    imageHidden,
    isFirst,
  } = props;
  const { sectionImageBorderRadiusOverride, sectionImageWidthOverride, isArticle } = useContext(SectionPageContext);

  const renderSectionItem = (sectionItem: any, index: number) => {
    if (!sectionItem) {
      return "";
    }

    switch (sectionItem.alias) {
      case "image": {
        const sectionImage = sectionItem as Image;
        return (
          <BNBox
            key={index}
            sx={{
              textAlign: "center",
              my: isArticle ? undefined : 2,
            }}
          >
            <Media
              AltText={sectionImage.alt}
              imagePath={sectionImage.url}
              styleOverride={{
                width: sectionImageWidthOverride ?? undefined,
                borderRadius: sectionImageBorderRadiusOverride ?? undefined,
              }}
              noWrapper={true}
              width={sectionImage.width}
              height={sectionImage.height}
              sx={{
                width: "32rem",
              }}
            />
          </BNBox>
        );
      }
      case "rte": {
        const rte = sectionItem as RTE;
        return <RichText key={index} html={rte.html} hidden={rte.hidden} />;
      }
      case "button": {
        const button = sectionItem as IButton;
        return <Button key={index} {...button} />;
      }
      case "sellingpoints": {
        const sellingpoints = sectionItem as ISellingPoints;
        return <SellingPoints key={index} {...sellingpoints} />;
      }
      case "price": {
        const price = sectionItem as IPrice;
        return <Prices key={index} {...price} />;
      }
      case "priceExampleComponent": {
        const priceExample = sectionItem as IPriceExample;
        return <PriceExample key={index} {...priceExample} />;
      }
      case "fAQList": {
        const fAQList = sectionItem as IFaq;
        return (
          <BNBox
            component={"section"}
            sx={{
              margin: "0 auto",
              padding: 0,
            }}
            key={index}
          >
            <Faq {...fAQList} />
          </BNBox>
        );
      }
      case "expandableList": {
        const expandableList = sectionItem as IExpandableList;
        return (
          <Faq
            key={index}
            list={expandableList.list.map((x, i) => ({
              id: `expandable-${i}`,
              question: x.title,
              answer: x.richText,
            }))}
          />
        );
      }
      case "slider": {
        const slider = sectionItem as ISlider;
        return <SliderSsrRenderOnly key={index} {...slider} />;
      }
      case "selectableContentList": {
        const selectableContentList = sectionItem as ISelectableContentList;
        return <SelectableContent key={index} {...selectableContentList} />;
      }
      case "googleReview": {
        return <GoogleReview key={index} content={sectionItem} />;
      }
    }
  };

  return (
    <SectionWrapper anchorId={anchorId} backgroundColor={backgroundColor?.label || "default"} noImage={!image}>
      {image && (
        <SectionWrapperImage
          imageHidden={imageHidden}
          sectionContainerStylesOverride={sectionContainerStylesOverride}
          gridOrder={imageOrderReverse ? 1 : 0}
        >
          <Media
            AltText={image.alt}
            imagePath={image.url}
            noWrapper={true}
            aspectRatio="1:1"
            width={800}
            maxWidth={800}
            forceEagerLoading={isFirst}
            sx={{
              width: "100%",
              [theme.breakpoints.up("md")]: {
                position: "absolute",
                top: 0,
                left: 0,
                height: "100%",
                objectFit: "cover",
              },
            }}
          />
        </SectionWrapperImage>
      )}

      <SectionWrapperText
        sectionContainerStylesOverride={sectionContainerStylesOverride}
        gridOrder={imageOrderReverse ? 0 : 1}
      >
        {list.map(renderSectionItem)}
      </SectionWrapperText>
    </SectionWrapper>
  );
};

export default Section;
